import { memo } from 'react'

import Analytics from 'utils/googleAnalytics'

import ToastService from 'services/toast.service'
import SaveService from 'services/save.service'

import {
  setLocalSortDirectionDescending,
  setLocalSorting,
  setLocalStack,
  setLocalView,
} from 'services/accountSettings.service'

import { store } from 'app-redux/store'
import { useActionless, useActions, useAppSelector } from 'app-redux/hooks'
import { useComponentDidMount } from 'hooks/componentDidMount'
import { useCanEditDeck } from 'app-redux/deck/selectors'

import { setDeckStateAction } from 'app-redux/deck/actions'
import { SET_ACTIVE_STATE } from 'app-redux/active/actions/types'

import { HUMAN_STACK, HUMAN_SORT, CardType, VIEW } from 'types/deck'

import CollapsibleContentContainer from 'components/elements/CollapsibleContentContainer'
import ChangePriceSource from 'components/deckPage/filtersAndControls/ChangePriceSource'
import QuickAddCard from 'components/cardSearchPanel/searchFormElements/quickAddCard'
import ViewExtras from 'components/deckPage/filtersAndControls/ViewExtras'
import PhatButton from 'components/formElements/PhatButton'
import PhatDropdown from 'components/formElements/PhatDropdown'
import Icon from 'components/elements/Icon'
import DeckFilter from 'components/deckPage/filtersAndControls/DeckFilter'
import DeckLayoutPresenter from 'components/deckPage/DeckLayoutPresenter'

import styles from './filterBar.module.scss'
import { useMobileControlToggles } from 'hooks/useMobileControlToggles.module'
import DeckViewAsDropdown from '../DeckViewAsDropdown'

const stackOptions = Object.keys(HUMAN_STACK).map(text => ({
  text,
  key: HUMAN_STACK[text],
  value: HUMAN_STACK[text],
}))

const sortOptions = Object.keys(HUMAN_SORT).map(text => ({
  text,
  key: HUMAN_SORT[text],
  value: HUMAN_SORT[text],
}))

const viewOptions = [
  { text: 'Stacks', value: VIEW.CARD },
  { text: 'Text', value: VIEW.TEXT },
  // { text: 'Brewer', value: VIEW.BREWING },
  { text: 'Grid', value: VIEW.GRID },
  // { text: 'Scroll', value: VIEW.SCROLL }, // Commenting out so people stop choosing it, we can remove VIEW.SCROLL later
  { text: 'Table', value: VIEW.SPREADSHEET },
]

const DeckFilters = memo(() => {
  const mounted = useComponentDidMount()

  const [mobileLayoutControls] = useMobileControlToggles()
  const canEditDeck = useCanEditDeck()

  const stack = useAppSelector(state => state.deck.stack)
  const sort = useAppSelector(state => state.deck.sorting)
  const sortDirectionDescending = useAppSelector(state => state.deck.sortDirectionDescending)
  const view = useAppSelector(state => state.deck.view)
  const searchOpen = useAppSelector(state => state.active.searchOpen)

  const [setActiveState] = useActionless(SET_ACTIVE_STATE)

  const [setDeckState] = useActions(setDeckStateAction)

  const hasAnyMobileControlsEnabled =
    mobileLayoutControls.deckFilter ||
    mobileLayoutControls.prices ||
    mobileLayoutControls.quickAdd ||
    mobileLayoutControls.view ||
    mobileLayoutControls.sort

  const handleStackChange = (_e: any, { value }: any) => {
    setDeckState({ stack: value })
    setLocalStack(value)
  }

  const handleSortChange = (_e: any, { value }: any) => {
    setDeckState({ sorting: value })
    setLocalSorting(value)
  }
  const handleViewChange = (_e: any, { value }: any) => {
    setDeckState({ view: value })
    setLocalView(value)
  }

  const handleChangeSortDirection = () => {
    setDeckState({ sortDirectionDescending: !sortDirectionDescending })
    setLocalSortDirectionDescending(!sortDirectionDescending)
  }

  return (
    <>
      <CollapsibleContentContainer name="Deck filters" className={styles.container}>
        <div className={styles.controls}>
          <div className={`${styles.addCardSection} ${styles.elevateOnEmptyDeckOverlay}`}>
            <label className={styles.header}>Add card</label>

            <PhatButton color="orange" onClick={() => setActiveState({ searchOpen: !searchOpen })}>
              <Icon name="search" />
              Card search
            </PhatButton>

            {canEditDeck && <QuickAddCard onCardSelected={onQuickAddCard} />}
          </div>

          <div className={styles.viewsAndSorts}>
            <DeckViewAsDropdown />

            <PhatDropdown
              fixedMenu
              matchMenuWidthToParent
              header="Group by"
              icon="clone outline"
              value={stack}
              triggerWrapperClassName={styles.dropdowns}
              options={stackOptions.map(opt => ({
                label: opt.text,
                id: `${opt.value}`,
                onClick: () => handleStackChange(null, { value: opt.value }),
              }))}
            />

            <PhatDropdown
              fixedMenu
              matchMenuWidthToParent
              header="Sort by"
              icon={sortDirectionDescending ? 'sort amount down' : 'sort amount up'}
              value={sort}
              triggerWrapperClassName={styles.dropdowns}
              options={[
                {
                  label: 'Order descending',
                  onChange: handleChangeSortDirection,
                  type: 'checkbox',
                  checked: sortDirectionDescending,
                },
                { type: 'spacer' },
                ...sortOptions.map(opt => ({
                  label: opt.text,
                  id: `${opt.value}`,
                  onClick: () => handleSortChange(null, { value: opt.value }),
                })),
              ]}
            />

            <ChangePriceSource />
          </div>

          <DeckFilter primaryFilter />
        </div>
      </CollapsibleContentContainer>

      <DeckLayoutPresenter className={styles.layoutContainer} />

      {hasAnyMobileControlsEnabled && (
        <div className={styles.mobileToolbar}>
          {mobileLayoutControls.quickAdd && canEditDeck && <QuickAddCard onCardSelected={onQuickAddCard} />}
          {mobileLayoutControls.deckFilter && <DeckFilter />}

          {mobileLayoutControls.view && (
            <PhatDropdown
              fixedMenu
              matchMenuWidthToParent
              header="View as"
              icon="eye"
              value={view}
              triggerWrapperClassName={styles.dropdowns}
              options={viewOptions.map(opt => ({
                label: opt.text,
                id: `${opt.value}`,
                onClick: () => handleViewChange(null, { value: opt.value }),
              }))}
              extraOptionsElement={<ViewExtras />}
            />
          )}

          {mobileLayoutControls.group && (
            <PhatDropdown
              fixedMenu
              matchMenuWidthToParent
              header="Group by"
              icon="clone outline"
              value={stack}
              triggerWrapperClassName={styles.dropdowns}
              options={stackOptions.map(opt => ({
                label: opt.text,
                id: `${opt.value}`,
                onClick: () => handleStackChange(null, { value: opt.value }),
              }))}
            />
          )}

          {mobileLayoutControls.sort && (
            <PhatDropdown
              fixedMenu
              matchMenuWidthToParent
              header="Sort by"
              icon={sortDirectionDescending ? 'sort amount down' : 'sort amount up'}
              value={sort}
              triggerWrapperClassName={styles.dropdowns}
              options={[
                {
                  label: 'Order descending',
                  onChange: handleChangeSortDirection,
                  type: 'checkbox',
                  checked: sortDirectionDescending,
                },
                { type: 'spacer' },
                ...sortOptions.map(opt => ({
                  label: opt.text,
                  id: `${opt.value}`,
                  onClick: () => handleSortChange(null, { value: opt.value }),
                })),
              ]}
            />
          )}

          {mobileLayoutControls.prices && <ChangePriceSource />}
        </div>
      )}
    </>
  )
})

export default DeckFilters

export const onQuickAddCard = (card: CardType) => {
  const redux = store.getState()

  const { categories, cardMap } = redux.deck

  const { quickAddOptions } = redux.cacheables

  const matchingCardInDeck = Object.values(cardMap).find(c => {
    if (quickAddOptions.matchExistingBy === 'never') return false

    if (quickAddOptions.matchExistingBy === 'name') return c.name === card.name
    if (quickAddOptions.matchExistingBy === 'uid') return c.uid === card.uid

    return false
  })

  if (matchingCardInDeck && !quickAddOptions.incrementOnDuplicate)
    return Promise.resolve(
      ToastService.create(
        `${card.name}${
          quickAddOptions.matchExistingBy === 'uid' ? ` (${card.setCode})` : ''
        } already is in your deck. You can disable the setting preventing adding duplicate cards via the quick add settings. Change the "Match cards by" setting to "Never" to always add cards from Quick add regardles of whether or not they're already in your deck.`,
        'Add skipped',
        'warning',
      ),
    )

  if (matchingCardInDeck) {
    const updatedCard = { ...matchingCardInDeck, qty: matchingCardInDeck.qty + 1 }

    return SaveService.save(updatedCard).then(() => Analytics.signalEvent('Incremented card from quick add'))
  }

  const updatedCard = { ...card, qty: card.qty || 1 }

  if (quickAddOptions.addToCategoryName) {
    const categoryToAdd = categories[quickAddOptions?.addToCategoryName]?.name || null

    if (categoryToAdd) updatedCard.categories = [categoryToAdd]
  }

  return SaveService.save(updatedCard).then(() => Analytics.signalEvent('Added card from quick add'))
}
